import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Paper,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import classes from '../simulator.module.scss';
import React, { useState } from 'react';
import {
  CcfAccordion,
  CcfAccordionDetails,
  CcfAccordionSummary,
  CcfBox,
  CcfDatePickerIcon,
  CcfDoubleArrowIcon,
  CcfTextField,
} from '@nice-ccf/ui-controls';
import { contactHistory, getApplicationLocale } from '@nice-ccf/ui-components';
import DropDown from '../drop-down';
import simulatorStyles from '../simulator.styles';
import { makeStyles, createStyles } from '@mui/styles';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useSelector } from 'react-redux';

/* eslint-disable-next-line */
export interface ContactHistoryCardProps {
  contactHistoryList: contactHistory[] | undefined;
  handleInputChangeForHistory: (
    id: any | undefined,
    field: string,
    value: string,
    isFromContactHistory?: boolean
  ) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minWidth: '270px',
      height: '100%',
      margin: '0',
      '&$expanded': {
        minHeight: '45px',
        margin: '0',
      },
    },
    content: {
      margin: '0',
      '&$expanded': {
        margin: '0',
      },
    },
    expanded: {
      margin: '0',
    },
  })
);

/**
 * used to show contact history card in simulator
 * @param props - ContactHistoryCardProps
 * @example ` <ContactHistoryCard />`
 */
export function ContactHistoryCard(props: ContactHistoryCardProps) {
  const { contactHistoryList, handleInputChangeForHistory } = props;
  const styles = useStyles();
  const theme = useTheme();
  const simStyles = simulatorStyles(theme);
  const [expanded, setExpanded] = React.useState(null);
  const locale = useSelector(getApplicationLocale);
  const [isContactHistoryExpanded, setIsContactHistoryExpanded] =
    useState(false);

  /**
   * DatePickerIcon used to display date picker icon
   * @example <DatePickerIcon />
   */
  const DatePickerIcon = () => {
    return (
      <CcfBox sx={{ display: 'flex' }}>
        <CcfDatePickerIcon />
      </CcfBox>
    );
  };

  /**
   * used to show contact history card in simulator
   * @param props - ContactHistoryCardProps
   * @example ` <ContactHistoryCard />`
   */
  const handleChange = (panel: any) => (_: any, isExpanded: any) => {
    setExpanded(isExpanded ? panel : null);
    setExpanded((state) => {
      return state;
    });
  };

  /**
   * to save contact history  in local storage
   * @param args - none,
   * @example saveContactHistoryHandler()
   */
  const saveContactHistoryHandler = () => {
    localStorage.setItem(
      'contactHistoryLS',
      JSON.stringify(contactHistoryList)
    );
  };

  return (
    <Grid
      style={{ width: '100%', height: 'auto' }}
      className={classes.simulatorColumn}
    >
      <Paper elevation={3} className={classes.simulatorPaperWithFullheight}>
        <Card className={classes.heightAuto}>
          <CardHeader
            title={`Contact History`}
            onClick={() =>
              setIsContactHistoryExpanded(!isContactHistoryExpanded)
            }
          />
          {!contactHistoryList ? (
            <CardContent className={classes.centerAlign}>
              <Typography color="textSecondary">No Contact History.</Typography>
            </CardContent>
          ) : (
            isContactHistoryExpanded && (
              <>
                <CardContent
                  className={
                    classes.simulatorHistoryContent + ' ' + classes.fixedHeight
                  }
                >
                  {contactHistoryList?.map((contactHistory, index) => {
                    const contactHIstoryId = contactHistory.id;
                    return (
                      <CcfAccordion
                        square
                        sx={simStyles.accordionContainer}
                        expanded={expanded === contactHistory.id}
                        onChange={handleChange(contactHistory.id)}
                      >
                        <CcfAccordionSummary
                          expandIcon={
                            <CcfDoubleArrowIcon sx={simStyles.expandedIcon} />
                          }
                          aria-controls="detail-content"
                          id={`sm_contactHistory_${contactHistory.customer}_customerName`}
                          sx={simStyles.accordionHeader}
                        >
                          <Typography>{contactHistory.customer}</Typography>
                        </CcfAccordionSummary>
                        <CcfAccordionDetails classes={{ root: styles.root }}>
                          <CardActionArea key={'index'}>
                            <CardContent style={{ padding: '0px' }}>
                              <div
                                id={`sm_contactHistory_${contactHistory.customer}_time`}
                              >
                                <LocalizationProvider
                                  adapterLocale={locale}
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DateTimePicker
                                    label="Time"
                                    value={dayjs(contactHistory.time)}
                                    onChange={(newValue: any) =>
                                      handleInputChangeForHistory(
                                        contactHistory.id,
                                        'time',
                                        newValue.toISOString()
                                      )
                                    }
                                    renderInput={(props: any) => (
                                      <CcfTextField
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        style={{ marginBottom: '15px' }}
                                        className={classes.input}
                                        InputProps={{
                                          classes: { input: classes.p10 },
                                        }}
                                        id="time"
                                        InputLabelProps={{ shrink: true }}
                                        {...props}
                                      />
                                    )}
                                    components={{
                                      OpenPickerIcon: DatePickerIcon,
                                    }}
                                  />
                                </LocalizationProvider>
                              </div>

                              <CcfTextField
                                fullWidth
                                color="primary"
                                id={`sm_contactHistory_${contactHistory.customer}_customerName`}
                                style={{ marginBottom: '15px' }}
                                className={classes.input}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ classes: { input: classes.p10 } }}
                                value={contactHistory.customer}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleInputChangeForHistory(
                                    contactHIstoryId,
                                    'customer',
                                    event?.target.value
                                  )
                                }
                                label={'Customer Name'}
                                variant="outlined"
                              ></CcfTextField>
                              <CcfTextField
                                fullWidth
                                id={`sm_contactHistory_${contactHistory.customer}_notes`}
                                color="primary"
                                style={{ marginBottom: '15px' }}
                                className={classes.input}
                                InputProps={{ classes: { input: classes.p10 } }}
                                InputLabelProps={{ shrink: true }}
                                value={contactHistory.notes}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleInputChangeForHistory(
                                    contactHistory.id,
                                    'notes',
                                    event?.target.value
                                  )
                                }
                                label={'Notes'}
                                variant="outlined"
                              ></CcfTextField>
                              <br />
                              <CcfTextField
                                fullWidth
                                color="primary"
                                id={`sm_contactHistory_${contactHistory.customer}_tags`}
                                style={{ marginBottom: '15px' }}
                                className={classes.input}
                                InputProps={{ classes: { input: classes.p10 } }}
                                InputLabelProps={{ shrink: true }}
                                value={contactHistory.tags}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleInputChangeForHistory(
                                    contactHIstoryId,
                                    'tags',
                                    event?.target.value
                                  )
                                }
                                label={'Tags'}
                                variant="outlined"
                              ></CcfTextField>
                              <br />
                              <DropDown
                                {...{
                                  isFromContactHistory: true,
                                  customerHistoryId: contactHistory.id,
                                  currentValue: contactHistory.status,
                                  field: 'status',
                                  label: 'Status',
                                  options: [
                                    'New',
                                    'Open',
                                    'Pending',
                                    'Resolved',
                                    'Escalated',
                                    'Closed',
                                  ],
                                  handleInputChangeForHistory,
                                  id: `sm_contactHistory_${contactHistory.customer}_status_${contactHistory.status}`,
                                  stackName: `contactHistory_${contactHistory.customer}_status`,
                                }}
                              />
                              <br />
                              <DropDown
                                {...{
                                  isFromContactHistory: true,
                                  customerHistoryId: contactHistory.id,
                                  currentValue: contactHistory.skill,
                                  field: 'skill',
                                  label: 'Skill',
                                  options: [
                                    'Inbound Voice',
                                    'Outbound Voice',
                                    'Chat',
                                    'Email',
                                    'Instagram',
                                  ],
                                  handleInputChangeForHistory,
                                  id: `sm_contactHistory_${contactHistory.customer}_skill_${contactHistory.skill}`,
                                  stackName: `contactHistory_${contactHistory.customer}_skill`,
                                }}
                              />
                            </CardContent>
                          </CardActionArea>
                          <Divider />
                        </CcfAccordionDetails>
                      </CcfAccordion>
                    );
                  })}
                </CardContent>
                <CardActions className={classes.simulatorSaveButton}>
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={saveContactHistoryHandler}
                    id={`sm_contactHistory_save`}
                  >
                    Save
                  </Button>
                </CardActions>
              </>
            )
          )}
        </Card>
      </Paper>
    </Grid>
  );
}

export default ContactHistoryCard;
